import React from "react";
import { CellHeader } from "./components/header/CellHeader";
import Label from "./components/cells/Label";
import MultiSelectFilter from "./components/filters/MultiSelectFilter";
import type {
  AppliedFilters,
  DateRange,
  DatePreset,
  FilterOption,
} from "./constants/config/filter";
import type { AnalyticsData } from "./types/data";
import DatePickerFilter from "./components/filters/DatePickerFilter";

export const renderHeader = (
  label: string,
  info: React.ReactNode,
  actions?: React.ReactNode,
): React.ReactNode => {
  return (
    <div className="et-cell-heading-layout px-3 flex flex-col items-start justify-center gap-1 w-full">
      <div className="et-cell-heading-actions flex flex-row items-center justify-center gap-2">
        <CellHeader label={label} info={info} />
        {actions}
      </div>
    </div>
  );
};

export const renderLabel = (
  content: React.ReactNode,
  className?: string,
): React.ReactNode => {
  return <Label className={`px-3 ${className}`}>{content}</Label>;
};

export const renderDateRangeFilter = ({
  value,
  onChange,
  onApply,
  onClear,
  presets,
  beforeActions,
}: {
  value: AppliedFilters[keyof AppliedFilters] | undefined;
  onChange: (value: AppliedFilters[keyof AppliedFilters] | undefined) => void;
  onApply: () => void;
  onClear: () => void;
  presets: DatePreset[];
  beforeActions?: React.ReactNode;
}): React.ReactNode => {
  return (
    <DatePickerFilter
      value={value as DateRange | undefined}
      onChange={(val) => onChange(val)}
      onApply={onApply}
      onClear={onClear}
      presets={presets}
      beforeActions={beforeActions}
    />
  );
};

export const renderMultiSelectFilter = ({
  data,
  value,
  onChange,
  onApply,
  onClear,
  getOptions,
  searchPlaceholder,
  showSearch,
  nameHint,
  committedValue,
}: {
  data: AnalyticsData[];
  value: AppliedFilters[keyof AppliedFilters] | undefined;
  onChange: (value: AppliedFilters[keyof AppliedFilters] | undefined) => void;
  onApply: () => void;
  onClear: () => void;
  getOptions: (data: AnalyticsData[]) => FilterOption[];
  searchPlaceholder?: string;
  showSearch?: boolean;
  nameHint?: string;
  committedValue?: AppliedFilters[keyof AppliedFilters];
}): React.ReactNode => {
  const selected = (value as string[]) || [];
  const options = getOptions(data);
  // A deep-linked group or lesson with no sessions appears in no row, so it is
  // absent from the options and could not otherwise be seen or unticked.
  //
  // Synthesized from the last APPLIED selection, not the live in-progress one:
  // unticking one of these rows changes `selected` immediately, and deriving
  // `missing` from that would make the row disappear before Apply, leaving no
  // way to re-tick it without closing and reopening the dropdown.
  const lastApplied = (committedValue as string[]) || selected;
  const missing = lastApplied
    .filter((v) => !options.some((option) => option.value === v))
    .map((v) => ({
      value: v,
      label: lastApplied.length === 1 && nameHint ? nameHint : v,
    }));
  const allOptions = missing.length > 0 ? [...missing, ...options] : options;
  return (
    <MultiSelectFilter
      options={allOptions}
      selectedValues={selected}
      onChange={(values) => onChange(values.length > 0 ? values : undefined)}
      onApply={onApply}
      onClear={onClear}
      searchPlaceholder={searchPlaceholder}
      showSearch={showSearch}
    />
  );
};

export const renderBucketFilter = ({
  value,
  onChange,
  onApply,
  onClear,
  buckets,
}: {
  value: AppliedFilters[keyof AppliedFilters] | undefined;
  onChange: (value: AppliedFilters[keyof AppliedFilters] | undefined) => void;
  onApply: () => void;
  onClear: () => void;
  buckets: string[];
}): React.ReactNode => {
  return (
    <MultiSelectFilter
      options={buckets.map((bucket) => ({ value: bucket, label: bucket }))}
      selectedValues={(value as string[]) || []}
      onChange={(values) => onChange(values.length > 0 ? values : undefined)}
      onApply={onApply}
      onClear={onClear}
      showSearch={false}
    />
  );
};
